import {useEffect, useRef, useState} from 'react'
import Loading from './Loading.tsx'
import {PrimaryButton} from './PrimaryButton.tsx'
import {FormError} from '../form/FormError.tsx'
import {cn} from '../../lib/utils.ts'
import {loadSaxonRuntime, parseUblXml, transformUblToHtml, UblRenderError} from './ubl/ublTransform.ts'

interface Props {
    xmlString?: string
    className?: string
    showToolbar?: boolean
    emptyMessage?: string
}

type Status = 'idle' | 'processing' | 'ready' | 'error'

export default function UblViewer({xmlString, className, showToolbar = true, emptyMessage = 'Geen UBL-document om weer te geven.'}: Props) {
    const [status, setStatus] = useState<Status>('idle')
    const [html, setHtml] = useState('')
    const [error, setError] = useState('')
    const iframeRef = useRef<HTMLIFrameElement>(null)

    useEffect(() => {
        let active = true

        if (!xmlString || !xmlString.trim()) {
            setStatus('idle')
            setHtml('')
            setError('')
            return
        }

        setStatus('processing')
        setError('')

        void (async () => {
            try {
                parseUblXml(xmlString)
                const saxon = await loadSaxonRuntime()
                const output = await transformUblToHtml(saxon, {sourceText: xmlString})
                if (!active) return
                setHtml(output)
                setStatus('ready')
            } catch (caught) {
                if (!active) return
                const message =
                    caught instanceof UblRenderError || caught instanceof Error
                        ? caught.message
                        : 'Kan het UBL-document niet weergeven.'
                setError(message)
                setStatus('error')
            }
        })()

        return () => {
            active = false
        }
    }, [xmlString])

    function handlePrint() {
        const frame = iframeRef.current
        if (!frame?.contentWindow) return
        frame.contentWindow.focus()
        frame.contentWindow.print()
    }

    function handleFrameLoad() {
        const frame = iframeRef.current
        const body = frame?.contentDocument?.body
        if (!frame || !body) return
        frame.style.height = `${body.scrollHeight}px`
    }

    if (status === 'error') {
        return (
            <div className={cn('ubl-viewer', className)}>
                <FormError error={error}/>
            </div>
        )
    }

    if (status === 'processing') {
        return (
            <div className={cn('ubl-viewer', className)}>
                <Loading message="Factuur wordt geladen…"/>
            </div>
        )
    }

    if (status === 'idle') {
        return <div className={cn('ubl-viewer text-sm text-gray-500', className)}>{emptyMessage}</div>
    }

    return (
        <div className={cn('ubl-viewer flex flex-col gap-3', className)}>
            {showToolbar && (
                <div className="flex justify-end">
                    <PrimaryButton title="Print" onClick={handlePrint}/>
                </div>
            )}
            <iframe
                ref={iframeRef}
                title="UBL invoice"
                srcDoc={html}
                onLoad={handleFrameLoad}
                sandbox="allow-same-origin allow-modals"
                className="w-full min-h-[600px] rounded-md border border-gray-200 bg-white"
            />
        </div>
    )
}
